import React from "react";
import {
  Text,
  Flex,
  Card,
  Link,
  Table,
  TableCell,
  TableRow,
  TableBody,
  Banner,
} from "@jsq/ds/core";
import Money from "../../Money";
import { AccountingPayableData } from "../types";
import { formatDate } from "../utils";
import {
  transactionDetailsCard,
  cardTitle,
  tableCell,
  tableRow,
  remainingToPayBanner,
} from "./styles_vanilla.css";

export type TransactionDetailsCardProps = {
  payable: AccountingPayableData;
  entityUrl?: string | null;
  onCreatePayment?: () => void;
};

export const TransactionDetailsCard: React.FC<TransactionDetailsCardProps> = ({
  payable,
  entityUrl,
  onCreatePayment,
}) => {
  const showRemainingToPay = payable.remainingToPay.amount > 0;
  const showCreatePaymentButton = onCreatePayment && showRemainingToPay;

  return (
    <Card className={transactionDetailsCard}>
      <Flex flexDirection="column">
        <Text className={cardTitle} variant="secondary-md">
          TRANSACTION DETAILS
        </Text>
        <Table condensed>
          <TableBody>
            <TableRow className={tableRow}>
              <TableCell className={tableCell}>
                <Text variant="secondary-md">Client</Text>
              </TableCell>
              <TableCell>
                <Text variant="regular-md">{payable.clientName}</Text>
              </TableCell>
            </TableRow>
            <TableRow className={tableRow}>
              <TableCell className={tableCell}>
                <Text variant="secondary-md">Investment entity</Text>
              </TableCell>
              <TableCell>
                {entityUrl ? (
                  <Link
                    href={entityUrl}
                    align="left"
                    icon="external-link"
                    iconPosition="right"
                    shouldOpenInNewTab
                  >
                    {payable.entityName}
                  </Link>
                ) : (
                  <Text variant="regular-md">{payable.entityName}</Text>
                )}
              </TableCell>
            </TableRow>
            <TableRow className={tableRow}>
              <TableCell className={tableCell}>
                <Text variant="secondary-md">Amount</Text>
              </TableCell>
              <TableCell>
                {payable.totalAmountPayable[0] && (
                  <Money money={payable.totalAmountPayable[0]} rounded={false} />
                )}
              </TableCell>
            </TableRow>
            <TableRow className={tableRow}>
              <TableCell className={tableCell}>
                <Text variant="secondary-md">Date</Text>
              </TableCell>
              <TableCell>
                <Text variant="regular-md">{formatDate(payable.date)}</Text>
              </TableCell>
            </TableRow>
            {payable.requestedPaymentDate && (
              <TableRow className={tableRow}>
                <TableCell className={tableCell}>
                  <Text variant="secondary-md">Requested payment date</Text>
                </TableCell>
                <TableCell>
                  <Text variant="regular-md">{formatDate(payable.requestedPaymentDate)}</Text>
                </TableCell>
              </TableRow>
            )}
            <TableRow className={tableRow}>
              <TableCell className={tableCell}>
                <Text variant="secondary-md">Positions</Text>
              </TableCell>
              <TableCell>
                {payable.payeeCount === 1 &&
                payable.singlePositionName &&
                payable.singlePositionUrl ? (
                  <Link
                    href={payable.singlePositionUrl}
                    align="left"
                    icon="external-link"
                    iconPosition="right"
                    shouldOpenInNewTab
                  >
                    {payable.singlePositionName}
                  </Link>
                ) : (
                  <Text variant="regular-md">{payable.payeeCount}</Text>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {showCreatePaymentButton && (
          <Banner
            className={remainingToPayBanner}
            variant="primary"
            data-testid="remaining-to-pay-banner"
            title=""
            description={
              <>
                There is <Money money={payable.remainingToPay} rounded={false} /> remaining to
                pay
              </>
            }
            isDescriptionJsx
            buttonProps={{
              children: "Create payment",
              variant: "primary",
              onClick: onCreatePayment,
              "data-testid": "create-payment-button",
            }}
          />
        )}
      </Flex>
    </Card>
  );
};
